import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { fDate } from 'src/utils/format/format-time';

import { Image } from 'src/components/image';
import { Iconify } from 'src/components/iconify';

import { BLOG_DETAILS } from '../constants/blog-copy';
import { CM, PUBLIC_TEXT, BLOG_READ_WIDTH, BLOG_COVER_WIDTH, BLOG_COVER_FRAME_SX } from '../constants/blog-layout';

import type { BlogPostAuthor } from '../scope/blog-post-scope';

// ----------------------------------------------------------------------

type PostDetailsHeroProps = {
  title: string;
  author?: BlogPostAuthor;
  coverUrl: string;
  createdAt: Date | null;
  tags: string[];
};

/**
 * 글 머리 — 흰 바탕 위 「블로그 목록」 · 주제 · 제목 · 글쓴이와 날짜(본문 폭), 그 아래 표지(한 단 넓게).
 * 표지가 없으면 표지 자리를 그리지 않는다. 글쓴이나 날짜가 없으면 그 줄을 그리지 않는다.
 */
export function PostDetailsHero({ title, author, coverUrl, createdAt, tags }: PostDetailsHeroProps) {
  return (
    <Box component="header">
      <Box sx={{ maxWidth: BLOG_READ_WIDTH, mx: 'auto' }}>
        <Box
          component={RouterLink}
          href={paths.home.blog}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: CM.textMuted, textDecoration: 'none', fontSize: PUBLIC_TEXT.eyebrow.fontSize, fontWeight: 600, '&:hover': { color: CM.primary } }}
        >
          <Iconify icon="solar:alt-arrow-left-linear" width={16} />
          {BLOG_DETAILS.backToList}
        </Box>

        {!!tags.length && (
          <Typography sx={{ ...PUBLIC_TEXT.eyebrow, color: CM.primary, mt: 3 }}>{tags.join(' · ')}</Typography>
        )}

        <Typography component="h1" sx={{ ...PUBLIC_TEXT.title, color: CM.textDark, mt: tags.length ? 1 : 3, whiteSpace: 'pre-line' }}>
          {title}
        </Typography>

        {author && createdAt && (
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 2 }}>
            <Avatar alt={author.name} src={author.avatarUrl} sx={{ width: 40, height: 40 }} />
            <Box>
              <Typography sx={{ ...PUBLIC_TEXT.body, fontWeight: 600, color: CM.textDark, lineHeight: 1.4 }}>{author.name}</Typography>
              <Typography sx={{ fontSize: PUBLIC_TEXT.eyebrow.fontSize, color: CM.textSubtle }}>{fDate(createdAt)}</Typography>
            </Box>
          </Stack>
        )}
      </Box>

      {coverUrl && (
        <Box sx={{ ...BLOG_COVER_FRAME_SX, maxWidth: BLOG_COVER_WIDTH, mx: 'auto', mt: { xs: 3, md: 4 } }}>
          <Image alt={title} src={coverUrl} ratio="16/9" sizes="(min-width: 1000px) 960px, 100vw" />
        </Box>
      )}
    </Box>
  );
}
